import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { clearSecret, getStoredSecret, probe, storeSecret } from "./api";
import { Tenants } from "./pages/Tenants";
import { Jobs } from "./pages/Jobs";
import { Files } from "./pages/Files";
import { Restores } from "./pages/Restores";
import { Savings } from "./pages/Savings";
import { Audit } from "./pages/Audit";
import { Retention } from "./pages/Retention";
import { Setup } from "./pages/Setup";

export function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!getStoredSecret()) { setAuthed(false); return; }
    probe().then(ok => setAuthed(ok)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return null;
  if (!authed) return <Gate onAuthed={() => setAuthed(true)} />;

  return (
    <div className="app">
      <nav className="sidebar">
        <h1>EssentialArchive</h1>
        <NavLink to="/tenants"  className={({ isActive }) => isActive ? "active" : ""}>Tenants</NavLink>
        <NavLink to="/jobs"     className={({ isActive }) => isActive ? "active" : ""}>Jobs</NavLink>
        <NavLink to="/files"    className={({ isActive }) => isActive ? "active" : ""}>Files</NavLink>
        <NavLink to="/restores" className={({ isActive }) => isActive ? "active" : ""}>Restores</NavLink>
        <NavLink to="/retention" className={({ isActive }) => isActive ? "active" : ""}>Retention</NavLink>
        <NavLink to="/savings"  className={({ isActive }) => isActive ? "active" : ""}>Savings</NavLink>
        <NavLink to="/audit"    className={({ isActive }) => isActive ? "active" : ""}>Audit</NavLink>
        <NavLink to="/setup"    className={({ isActive }) => isActive ? "active" : ""}>Setup</NavLink>
        <button
          className="signout"
          onClick={() => { clearSecret(); setAuthed(false); }}
        >Sign out</button>
      </nav>
      <main className="content">
        <Routes>
          <Route path="/"         element={<Navigate to="/tenants" replace />} />
          <Route path="/tenants"  element={<Tenants />} />
          <Route path="/jobs"     element={<Jobs />} />
          <Route path="/files"    element={<Files />} />
          <Route path="/restores" element={<Restores />} />
          <Route path="/retention" element={<Retention />} />
          <Route path="/savings"  element={<Savings />} />
          <Route path="/audit"    element={<Audit />} />
          <Route path="/setup"    element={<Setup />} />
          <Route path="*"         element={<Navigate to="/tenants" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Gate({ onAuthed }: { onAuthed: () => void }) {
  const [secret, setSecret] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    storeSecret(secret.trim());
    try {
      const ok = await probe();
      if (!ok) { clearSecret(); setErr("Admin secret rejected by Worker."); return; }
      onAuthed();
      nav("/tenants");
    } catch (e) {
      clearSecret();
      setErr(e instanceof Error ? e.message : "Login failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="gate">
      <form className="card" onSubmit={submit}>
        <h2>EssentialArchive</h2>
        <p className="muted">Enter the admin secret from your Worker's KV (key <code>_admin_secret</code>).</p>
        <input
          className="input"
          type="password"
          autoFocus
          value={secret}
          onChange={e => setSecret(e.target.value)}
          placeholder="Admin secret"
          style={{ marginBottom: ".75rem" }}
        />
        {err && <div className="err">{err}</div>}
        <button className="btn" type="submit" disabled={busy || !secret.trim()}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
